import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, groupBy, map, mergeMap, switchMap } from 'rxjs/operators';
import { closingNoteActions } from '../actions/closing-note.actions';
import { ClosingNoteService } from '../services/closing-note.service';

/** Ein Fehler, der dem Nutzer nichts sagt, sagt dem Entwickler auch nichts. */
function messageOf(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
        const body = error.error as { message?: string | string[] } | null;
        const message = body?.message;
        if (Array.isArray(message)) {
            return message.join(', ');
        }
        if (typeof message === 'string') {
            return message;
        }
        return `HTTP ${error.status}`;
    }
    return 'unknown error';
}

@Injectable()
export class ClosingNoteEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly service: ClosingNoteService,
    ) {}

    public readonly loadNotes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingNoteActions.loadNotes),
            // ⚠️ Erst GRUPPIEREN, dann `switchMap` — und zwar je Liste, nicht über alle.
            //
            // Vorher stand hier ein einzelnes `switchMap` über JEDE `loadNotes`-Aktion, mit der
            // richtigen Begründung (beim Umschalten zwischen den Registern darf nicht die Antwort
            // der vorherigen Art zuletzt eintreffen) und der falschen Reichweite: `switchMap`
            // bricht die laufende Anfrage ab, EGAL welche Liste sie holte. Die Abschluss-Seite
            // lädt beim Öffnen beide Register hintereinander, damit die Zahlen an den Reitern
            // stimmen — die zweite Aktion (TODO) hat die erste (COMMENT) damit abgebrochen. Am
            // Reiter stand deshalb „Kommentare 0", bis man ihn anklickte und die Liste ein zweites
            // Mal geholt wurde. Kein Fehler, keine Meldung: die Anfrage war einfach weg.
            //
            // Gruppiert nach Art UND danach, ob der Konto-Bereich fragt: das sind drei
            // unabhängige Listen, die einander nichts abbrechen dürfen. INNERHALB einer Gruppe
            // bleibt `switchMap` — dort ist das Abbrechen gerade richtig, weil eine zweite
            // Anfrage derselben Liste die erste ersetzt.
            //
            // REV-21369 — der Beleg-Viewer ist eine VIERTE unabhängige Liste und bekommt seine eigene
            // Gruppe; sonst bräche das Öffnen eines Belegs die Liste am Konto ab und umgekehrt.
            groupBy(({ kind, accountId, documentId }) =>
                `${kind}:${documentId !== undefined ? 'document' : accountId !== undefined ? 'account' : 'register'}`,
            ),
            mergeMap((perList$) =>
                perList$.pipe(
                    switchMap(({ projectId, kind, accountId, documentId }) =>
                        this.service
                            .list(projectId, kind, {
                                ...(accountId === undefined ? {} : { accountId }),
                                ...(documentId === undefined ? {} : { documentId }),
                            })
                            .pipe(
                                map((notes) =>
                                    closingNoteActions.loadNotesSuccess({
                                        projectId,
                                        kind,
                                        notes,
                                        ...(accountId === undefined ? {} : { accountId }),
                                        ...(documentId === undefined ? {} : { documentId }),
                                    }),
                                ),
                                catchError((error) =>
                                    of(
                                        closingNoteActions.loadNotesFailure({
                                            projectId,
                                            kind,
                                            error: messageOf(error),
                                            ...(accountId === undefined ? {} : { accountId }),
                                            ...(documentId === undefined ? {} : { documentId }),
                                        }),
                                    ),
                                ),
                            ),
                    ),
                ),
            ),
        ),
    );

    public readonly loadNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingNoteActions.loadNote),
            switchMap(({ projectId, kind, noteId }) =>
                this.service.byId(projectId, kind, noteId).pipe(
                    map((note) => closingNoteActions.loadNoteSuccess({ projectId, note })),
                    catchError((error) =>
                        of(
                            closingNoteActions.loadNoteFailure({
                                projectId,
                                noteId,
                                error: messageOf(error),
                                // ⚠️ 404 heisst „gibt es nicht", alles andere „liess sich nicht
                                // laden" (AC9). Ein geteilter Link auf einen geloeschten Eintrag
                                // ist der Normalfall und darf nicht als Stoerung erscheinen.
                                notFound: error instanceof HttpErrorResponse && error.status === 404,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    public readonly createNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingNoteActions.createNote),
            // `mergeMap` bei Schreibvorgaengen: zwei schnell hintereinander erfasste Notizen sind
            // zwei Notizen. `switchMap` wuerde die erste abbrechen und still verlieren.
            mergeMap(({ projectId, request }) =>
                this.service.create(projectId, request).pipe(
                    map((note) => closingNoteActions.createNoteSuccess({ projectId, note })),
                    catchError((error) =>
                        of(closingNoteActions.createNoteFailure({ projectId, error: messageOf(error) })),
                    ),
                ),
            ),
        ),
    );

    public readonly updateNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingNoteActions.updateNote),
            mergeMap(({ projectId, noteId, request }) =>
                this.service.update(projectId, noteId, request).pipe(
                    map((note) => closingNoteActions.updateNoteSuccess({ projectId, note })),
                    catchError((error) =>
                        of(closingNoteActions.updateNoteFailure({ projectId, noteId, error: messageOf(error) })),
                    ),
                ),
            ),
        ),
    );

    public readonly deleteNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingNoteActions.deleteNote),
            mergeMap(({ projectId, noteId }) =>
                this.service.remove(projectId, noteId).pipe(
                    map(() => closingNoteActions.deleteNoteSuccess({ projectId, noteId })),
                    catchError((error) =>
                        of(closingNoteActions.deleteNoteFailure({ projectId, noteId, error: messageOf(error) })),
                    ),
                ),
            ),
        ),
    );

    public readonly setDone$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingNoteActions.setNoteDone),
            mergeMap(({ projectId, noteId, done }) =>
                this.service.setDone(projectId, noteId, done).pipe(
                    map((note) => closingNoteActions.setNoteDoneSuccess({ projectId, note })),
                    catchError((error) =>
                        of(closingNoteActions.setNoteDoneFailure({ projectId, noteId, error: messageOf(error) })),
                    ),
                ),
            ),
        ),
    );

    public readonly reply$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingNoteActions.replyToNote),
            mergeMap(({ projectId, noteId, text, mentionUserIds }) =>
                this.service.reply(projectId, noteId, text, mentionUserIds).pipe(
                    map((note) => closingNoteActions.replyToNoteSuccess({ projectId, note })),
                    catchError((error) =>
                        of(closingNoteActions.replyToNoteFailure({ projectId, noteId, error: messageOf(error) })),
                    ),
                ),
            ),
        ),
    );
}
